import { call, put, select } from 'typed-redux-saga/macro';
import {
  selectTdo,
  selectContentTypeHasImage,
  selectGlobalSettings,
  selectBackgroundBlurEdits,
} from '../selectors';
import {
  selectUDRCollection,
  selectTextOverlayCollection,
  selectDetectionCollections,
  selectSelected,
} from '../selectors/view';
import { saveRedactedImageAsset } from '@helpers/saveAsset';
import {
  updateLatestRedactedVersionOnDetails,
  deleteAssetById,
  uploadImageAuditLog,
} from '../services';
import {
  exportRedactedImage,
  canExportImage,
} from '@common/utils/exportRedactedImage';
import { REDACTING_FILE } from '@common-modules/redactFile';
import {
  ADD_AUDIT_LOG_ASSET_TO_TDO,
  ADD_REDACTED_MEDIA_ASSET_TO_TDO,
  REMOVE_REDACTED_MEDIA_ASSETS_FROM_TDO,
  REMOVE_AUDIT_LOG_ASSETS_FROM_TDO,
} from '../actions';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { sagaIntl } from '@i18n';
import { GroupedBoundingPoly, search } from '@worker';
import { modules } from '@veritone/glc-redux';
import type { BlurLevel, DetailTDO } from '@common-modules/mediaDetails/models';

const { user: userModule } = modules;

// Redacted images are always exported as .jpeg, so strip an existing JPEG
// extension off the source name before appending our own suffix. Other
// extensions are left intact (they become part of the base name).
const JPEG_EXTENSIONS = ['.jpeg', '.jpg'];
const removeJpegExtension = (filename: string) => {
  const lower = filename.toLowerCase();
  const extension = JPEG_EXTENSIONS.find((ext) => lower.endsWith(ext));
  return extension ? filename.slice(0, -extension.length) : filename;
};

export function* isImageFile() {
  return Boolean(yield* select(selectContentTypeHasImage));
}

// Load a fresh HTMLImageElement from a URL. crossOrigin="anonymous" matches
// the player so the browser cache hits the same response and the canvas stays
// untainted for export.
function loadImageFromUrl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image from ${src}`));
    img.src = src;
  });
}

// Collection keys to skip during normal iteration — poim is dropped from the new
// format; vehicle shares the 'licensePlate' collection and is counted separately
// in the licensePlate branch below
const DETECTION_KEY_SKIP = new Set(['poim', 'vehicle']);

function* computeMetrics(): Generator<any, Record<string, unknown>, any> {
  const detectionCollections = yield* select(selectDetectionCollections);
  const udrCollection = yield* select(selectUDRCollection);
  const selectedPolyGroups = yield* select(selectSelected);

  const metrics: Record<string, unknown> = {
    metricsFormatVersion: 1.1,
    views: 0,
    downloads: 0,
  };

  if (detectionCollections) {
    for (const [type, coll] of Object.entries(detectionCollections)) {
      if (DETECTION_KEY_SKIP.has(type) || !coll?.collection) {
        continue;
      }

      const polys = search(coll.collection, 0);
      const selected = polys.filter((poly) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const polyAny = poly as any;
        return (
          selectedPolyGroups[poly.groupId] ||
          selectedPolyGroups[polyAny.segmentId] ||
          selectedPolyGroups[polyAny.id]
        );
      });

      if (type === 'licensePlate') {
        // vehicle and licensePlate detections share one collection — split by poly.type
        const plateCount = new Set(
          selected
            .filter((p) => p.type === 'licensePlate')
            .map((p) => p.groupId)
        ).size;
        const vehicleCount = new Set(
          selected.filter((p) => p.type === 'vehicle').map((p) => p.groupId)
        ).size;
        if (plateCount > 0) {
          metrics.plate = { count: plateCount };
        }
        if (vehicleCount > 0) {
          metrics.vehicle = { count: vehicleCount };
        }
      } else {
        const count = new Set(selected.map((p) => p.groupId)).size;
        if (count > 0) {
          metrics[type] = { count };
        }
      }
    }
  }

  if (udrCollection?.collection) {
    const udrPolys = search(udrCollection.collection, 0);
    const selectedUDRs = udrPolys.filter((poly) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const polyAny = poly as any;
      return (
        selectedPolyGroups[poly.groupId] ||
        selectedPolyGroups[polyAny.segmentId] ||
        selectedPolyGroups[polyAny.id]
      );
    });
    const count = new Set(selectedUDRs.map((p) => p.groupId)).size;
    if (count > 0) {
      metrics.udr = { count };
    }
  }

  return metrics;
}

function* getAllBoundingPolys(): Generator<any, GroupedBoundingPoly[], any> {
  const currentTime = 0;
  const detectionCollections = yield* select(selectDetectionCollections);
  const udrCollection = yield* select(selectUDRCollection);
  const textOverlayCollection = yield* select(selectTextOverlayCollection);
  const selectedPolyGroups = yield* select(selectSelected);

  let allPolys: GroupedBoundingPoly[] = [];

  if (detectionCollections) {
    for (const coll of Object.values(detectionCollections)) {
      if (coll?.collection) {
        const polys = search(coll.collection, currentTime);
        const selectedPolys = polys.filter((poly) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const polyAny = poly as any;
          return (
            selectedPolyGroups[poly.groupId] ||
            selectedPolyGroups[polyAny.segmentId] ||
            selectedPolyGroups[polyAny.id]
          );
        });
        allPolys = allPolys.concat(selectedPolys);
      }
    }
  }

  if (udrCollection?.collection) {
    const udrPolys = search(udrCollection.collection, currentTime);
    const selectedUDRs = udrPolys.filter((poly) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const polyAny = poly as any;
      return (
        selectedPolyGroups[poly.groupId] ||
        selectedPolyGroups[polyAny.segmentId] ||
        selectedPolyGroups[polyAny.id]
      );
    });
    allPolys = allPolys.concat(selectedUDRs);
  }

  if (textOverlayCollection?.collection) {
    const textPolys = search(textOverlayCollection.collection, currentTime);
    const selectedTextPolys = textPolys.filter((poly) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const polyAny = poly as any;
      return (
        selectedPolyGroups[poly.groupId] ||
        selectedPolyGroups[polyAny.segmentId] ||
        selectedPolyGroups[polyAny.id]
      );
    });
    allPolys = allPolys.concat(selectedTextPolys);
  }

  return allPolys;
}

/**
 * The version number the next image burn-in will produce.
 *
 * Extracted from `redactImageFile` so it can be tested directly: VE-27738 makes
 * this number a claim about the file, not just part of a filename — the caller
 * labels the redaction data with whatever the output ends up stamped with, so
 * the whole verdict rests on it.
 *
 * Derived here, once, and handed back out on the result. Nothing else may derive
 * it: two derivations can disagree, and a disagreement reads as a permanently
 * stale file — the very defect this ticket is about.
 *
 * Prefer the MAX `details.version` across surviving outputs; fall back to
 * `details.latestRedactedMediaVersion` on the TDO so numbering does not reset if
 * every prior output was manually deleted, or predates version tracking.
 */
export function computeNextImageVersion(tdo: DetailTDO): number {
  const existingAssets = tdo.redactedMediaAssets?.records || [];
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const persistedVersion =
    (tdo.details as any)?.latestRedactedMediaVersion ?? 0;
  let version = 1;

  if (existingAssets.length > 0) {
    const versions = existingAssets
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .map((asset) => asset.details?.version || 0)
      .filter((v) => typeof v === 'number' && v > 0);

    if (versions.length > 0) {
      version = Math.max(...versions) + 1;
    }
  }

  if (typeof persistedVersion === 'number' && persistedVersion >= version) {
    version = persistedVersion + 1;
  }

  return version;
}

/**
 * Burn the editor's selected redactions into the image and upload the result as
 * a `redacted-media` asset, then rotate away the outputs it supersedes.
 *
 * Returns the `version` it stamped on the output. The caller labels the
 * redaction data with exactly that number (VE-27738), so the two sides of the
 * staleness comparison cannot drift: only this function decides the version.
 */
export function* redactImageFile() {
  const intl = sagaIntl();
  const tdo = yield* select(selectTdo);

  if (!tdo) {
    return { success: false, assetId: undefined, version: undefined };
  }

  const tdoId = tdo.id;

  try {
    yield* put(REDACTING_FILE({ tdoId }));

    // Load a fresh image element from the TDO's primary asset URL rather than
    // querying the DOM. This decouples the saga from the media player's markup
    // (test ids / alt text) and lets the export run even if the player is
    // unmounted. The browser cache typically satisfies this load from the
    // response already fetched for the player.
    const imageSrc = tdo.primaryAsset?.signedUri;
    if (!imageSrc) {
      throw new Error('No image URL available on the TDO primaryAsset');
    }
    const imageElement = yield* call(loadImageFromUrl, imageSrc);

    const width = imageElement.naturalWidth;
    const height = imageElement.naturalHeight;
    const checkResult = canExportImage(width, height);

    if (!checkResult.canExport) {
      throw new Error(checkResult.reason || 'Cannot export image');
    }

    const boundingPolyList = yield* call(getAllBoundingPolys);
    const metrics = yield* call(computeMetrics);

    const globalSettings = yield* select(selectGlobalSettings);
    const objectTypeEffects = globalSettings?.objectTypeEffects || {};
    const backgroundBlurEdits = yield* select(selectBackgroundBlurEdits);
    let backgroundBlurLevel: BlurLevel | undefined;
    const exportTimeMs = 0;
    for (const blurSlice of backgroundBlurEdits) {
      if (exportTimeMs >= blurSlice[0] && exportTimeMs <= blurSlice[1]) {
        backgroundBlurLevel = blurSlice[2]?.blurLevel ?? 5;
        break;
      }
    }

    const exportFormat = 'jpeg' as const;
    const exportContentType = `image/${exportFormat}` as const;
    const exportResult = yield* call(() =>
      exportRedactedImage(
        imageElement,
        boundingPolyList,
        objectTypeEffects,
        backgroundBlurLevel,
        {
          format: exportFormat,
          quality: 0.95,
        }
      )
    );

    if (!exportResult || !exportResult.blob) {
      throw new Error('Failed to generate redacted image blob');
    }

    // saveRedactedImageAsset still takes a getState() function for config/auth
    // lookups. Snapshotting state here is safe because those values don't
    // change mid-saga. Other operations now use thunks via `put`, which get a
    // real dispatch + getState through redux-thunk middleware.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const getState = yield* select((state: any) => () => state);

    const existingAssets = tdo.redactedMediaAssets?.records || [];
    const existingAuditLogAssets = tdo.auditLogAssets?.records || [];
    const version = computeNextImageVersion(tdo);

    const tdoNameWithoutExtension = removeJpegExtension(tdo.name || 'redacted');
    // Background blur redacts the image even with zero polygons, so the export
    // is only truly unredacted when there are no polys AND no blur was applied.
    // Match exportRedactedImage's render condition (level > 0) so the label
    // reflects what was actually drawn.
    const hasRedactions =
      boundingPolyList.length > 0 ||
      (backgroundBlurLevel !== undefined && backgroundBlurLevel > 0);
    const redactionLabel = hasRedactions ? 'REDACTED' : 'NO_REDACTIONS';
    const fileName = `${tdoNameWithoutExtension}[${redactionLabel}].v${version}.jpeg`;

    const user = userModule.selectUser(getState());

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result: any = yield* call(() =>
      saveRedactedImageAsset(
        {
          tdoId,
          contentType: exportContentType,
          type: 'redacted-media',
          name: fileName,
          width: exportResult.width,
          height: exportResult.height,
          duration: 0,
          version,
          email: user.email || '',
          checkpoint: undefined,
          metrics,
        },
        exportResult.blob,
        getState
      )
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const assetId: string = result?.data?.createAsset?.id || '';
    if (!assetId) {
      throw new Error('Redacted image asset creation returned no id');
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const assetModifiedDateTime: string =
      result?.data?.createAsset?.modifiedDateTime || new Date().toISOString();

    // Optimistically add the new asset to state so the Redacted Files tab
    // becomes enabled immediately, before fetchRedactedMedia returns. Without
    // this the tab stays disabled (no records) and MUI won't select it.
    yield* put(
      ADD_REDACTED_MEDIA_ASSET_TO_TDO({
        id: assetId,
        name: fileName,
        contentType: exportContentType,
        modifiedDateTime: assetModifiedDateTime,
        details: {
          version,
          width: exportResult.width,
          height: exportResult.height,
          metrics,
        },
      })
    );

    // Persist the new version on the TDO so a future redact can resume numbering
    // even if every prior redacted asset is manually deleted. The thunk uses
    // real dispatch/getState (via redux-thunk middleware), so callGraphQLApi's
    // FETCH_UPDATE_TDO_DETAILS_SUCCESS action reaches the reducer and merges
    // the returned details into state.tdo without requiring a TDO refetch.
    yield* put(updateLatestRedactedVersionOnDetails(version));

    // Mirror the server-side blur engine's audit-log behaviour: collect every
    // redact audit event for the TDO and upload them as a redact-audit-log CSV
    // asset linked back to the redacted-media asset we just created.
    // Best-effort: uploadImageAuditLog swallows its own errors so a failed
    // audit log never aborts the surrounding redact flow. call() is guaranteed
    // to await the Promise; the returned asset is dispatched synchronously so
    // ADD_AUDIT_LOG_ASSET_TO_TDO is in state before SELECT_REDACTED_FILES_TAB
    // fires.
    const auditLogAsset = yield* call(
      uploadImageAuditLog,
      tdoId,
      assetId,
      version,
      fileName,
      getState
    );
    if (auditLogAsset) {
      yield* put(ADD_AUDIT_LOG_ASSET_TO_TDO(auditLogAsset));
    }

    if (existingAssets.length > 0) {
      const assetsToDelete = existingAssets.filter((a) => a.id !== assetId);

      if (assetsToDelete.length > 0) {
        // existingAuditLogAssets was captured before the new audit log was
        // created, so every record in it is "old" and can be deleted wholesale.
        // Avoids fragile details.redactedMediaAssetId matching and handles the
        // case where the initial TDO load only fetched limit:1.
        const auditLogsToDelete = existingAuditLogAssets;

        // Delete from server (fire-and-forget; failures are logged internally).
        for (const asset of assetsToDelete) {
          yield* put(deleteAssetById(asset.id));
        }
        for (const log of auditLogsToDelete) {
          yield* put(deleteAssetById(log.id));
        }

        // Remove from Redux state immediately so the delete modal shows the
        // correct count without waiting for fetchRedactedMedia to return.
        const deletedMediaIds = (assetsToDelete as Array<{ id: string }>).map(
          (a) => a.id
        );
        yield* put(REMOVE_REDACTED_MEDIA_ASSETS_FROM_TDO(deletedMediaIds));
        if (auditLogsToDelete.length > 0) {
          const deletedLogIds = auditLogsToDelete.map((l) => l.id);
          yield* put(REMOVE_AUDIT_LOG_ASSETS_FROM_TDO(deletedLogIds));
        }
      }
    }

    return {
      success: true,
      assetId,
      version,
    };
  } catch (error) {
    let errorMessage = 'Unknown error';
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'object' && error !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      errorMessage =
        (error as any).errors?.[0]?.message ||
        (error as any).message ||
        JSON.stringify(error);
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage(
          {
            id: 'imageRedactionFailed',
            defaultMessage: 'Failed to redact image: {error}',
          },
          { error: errorMessage }
        ),
        variant: 'error',
      })
    );

    throw error;
  }
}
